import type { SingPitchId } from './clingCantaConfig';
import { noteVisual } from './clingCantaEngine';
import { midiNearPitch } from './juniorPitchDetector';
import { lerp } from './clingCantaPitch';
import {
  CLING_PIANO_ROLL_PITCHES,
  warmupSequence,
} from './clingNoteSelectionConfig';
import {
  CLING_COLLECT_RADIUS_X,
  CLING_COLLECT_RADIUS_Y,
  CLING_GRAVITY_PX_MS,
  clingScreenX,
  exploreFlyZoneBounds,
  exploreLaneBandHeight,
  exploreLaneBandTop,
  explorePitchOrMidiToFlyY,
  groundY,
} from './clingWorldConfig';

export type ExplorePhase = 'introDemo' | 'countdown' | 'play' | 'finished';

export interface ExploreCollectible {
  id: string;
  worldX: number;
  pitchId: SingPitchId;
  fill: string;
  color: string;
  label: string;
  collected: boolean;
  hit: boolean;
  missed: boolean;
}

export interface IntroScrollNote {
  id: string;
  pitchId: SingPitchId;
  x: number;
  width: number;
  played: boolean;
  fill: string;
  color: string;
  label: string;
}

export interface ClingExploreLevel {
  id: string;
  title: string;
  description: string;
  emoji: string;
  scrollSpeed: number;
  displayLanes: SingPitchId[];
  activeLanes: SingPitchId[];
  items: { pitchId: SingPitchId }[];
}

export interface ExploreGameState {
  phase: ExplorePhase;
  level: ClingExploreLevel;
  scrollX: number;
  lengthWorld: number;
  clingY: number;
  clingTargetY: number;
  elapsedMs: number;
  countdownBeat: number;
  score: number;
  items: ExploreCollectible[];
  introNotes: IntroScrollNote[];
  feedback: 'collect' | 'miss' | null;
  feedbackUntil: number;
}

export const EXPLORE_NOTE_GAP = 440;
export const EXPLORE_NOTE_LEAD = 520;
const INTRO_NOTE_GAP = 300;
const INTRO_NOTE_WIDTH = 52;
const INTRO_LEAD_PX = 280;
const INTRO_HIT_WINDOW_PX = 32;

export const CLING_EXPLORE_LEVELS: ClingExploreLevel[] = [
  {
    id: 'cueva-inicial',
    title: 'Cueva de los Cristales',
    description: 'Escucha a Cling cantar y luego recoge las notas',
    emoji: '🗺️',
    scrollSpeed: 0.11,
    displayLanes: CLING_PIANO_ROLL_PITCHES,
    activeLanes: ['Do5', 'Sol4', 'Mi4', 'Do4'],
    items: [
      { pitchId: 'Do4' },
      { pitchId: 'Mi4' },
      { pitchId: 'Sol4' },
      { pitchId: 'Mi4' },
      { pitchId: 'Do5' },
      { pitchId: 'Sol4' },
      { pitchId: 'Mi4' },
      { pitchId: 'Do4' },
      { pitchId: 'Do5' },
    ],
  },
];

let itemCounter = 0;

function buildItems(level: ClingExploreLevel): ExploreCollectible[] {
  return level.items.map((it) => {
    const visual = noteVisual(it.pitchId);
    return {
      id: `c-${itemCounter++}`,
      worldX: 0,
      pitchId: it.pitchId,
      fill: visual.fill,
      color: visual.color,
      label: visual.label,
      collected: false,
      hit: false,
      missed: false,
    };
  });
}

function spawnIntroNotes(sequence: SingPitchId[], stageWidth: number): IntroScrollNote[] {
  const cx = clingScreenX(stageWidth);
  const startX = cx + INTRO_LEAD_PX + Math.max(120, stageWidth - cx);
  return sequence.map((pitchId, i) => {
    const vis = noteVisual(pitchId);
    return {
      id: `intro-${i}-${pitchId}`,
      pitchId,
      x: startX + i * INTRO_NOTE_GAP,
      width: INTRO_NOTE_WIDTH,
      played: false,
      ...vis,
    };
  });
}

export function layoutExploreItems(
  items: ExploreCollectible[],
  stageWidth: number
): { items: ExploreCollectible[]; lengthWorld: number } {
  const cx = clingScreenX(stageWidth);
  const firstX = cx + EXPLORE_NOTE_LEAD + stageWidth * 0.35;
  const laid = items.map((item, i) => ({
    ...item,
    worldX: firstX + i * EXPLORE_NOTE_GAP,
    collected: false,
    hit: false,
    missed: false,
  }));
  const lastX = laid[laid.length - 1]?.worldX ?? firstX;
  return { items: laid, lengthWorld: lastX + stageWidth + EXPLORE_NOTE_GAP };
}

export function createExploreState(
  level: ClingExploreLevel,
  stageWidth = 640,
  stageHeight = 360
): ExploreGameState {
  const mid = level.activeLanes[Math.floor(level.activeLanes.length / 2)] ?? 'Mi4';
  const clingY = exploreDisplayPitchY(mid, level.displayLanes, stageHeight);
  const introSeq = warmupSequence(level.activeLanes);
  return {
    phase: 'introDemo',
    level,
    scrollX: 0,
    lengthWorld: 0,
    clingY,
    clingTargetY: clingY,
    elapsedMs: 0,
    countdownBeat: 3,
    score: 0,
    items: buildItems(level),
    introNotes: spawnIntroNotes(introSeq, stageWidth),
    feedback: null,
    feedbackUntil: 0,
  };
}

export function beginExploreCountdown(state: ExploreGameState): ExploreGameState {
  return {
    ...state,
    phase: 'countdown',
    elapsedMs: 0,
    countdownBeat: 3,
    scrollX: 0,
    introNotes: [],
    feedback: null,
    feedbackUntil: 0,
  };
}

export function beginExplorePlay(
  state: ExploreGameState,
  stageWidth: number,
  stageHeight: number
): ExploreGameState {
  const active = state.level.activeLanes;
  const mid = active[Math.floor(active.length / 2)]!;
  const y = exploreDisplayPitchY(mid, state.level.displayLanes, stageHeight);
  const { items, lengthWorld } = layoutExploreItems(state.items, stageWidth);
  return {
    ...state,
    phase: 'play',
    elapsedMs: 0,
    scrollX: 0,
    lengthWorld,
    items,
    introNotes: [],
    clingY: y,
    clingTargetY: y,
    countdownBeat: 0,
    feedback: null,
    feedbackUntil: 0,
  };
}

export interface ExploreTickResult {
  state: ExploreGameState;
  finished: boolean;
  visualDirty: boolean;
  callSoundPitch: SingPitchId | null;
  collectedNoteIndex: number | null;
}

function pitchMatchesCollect(
  voiceMidi: number | null,
  voicePitch: SingPitchId | null,
  target: SingPitchId
): boolean {
  if (voiceMidi != null) return midiNearPitch(voiceMidi, target);
  return voicePitch === target;
}

function midActiveY(state: ExploreGameState, stageHeight: number): number {
  const { activeLanes, displayLanes } = state.level;
  const mid = activeLanes[Math.floor(activeLanes.length / 2)] ?? activeLanes[0]!;
  return exploreDisplayPitchY(mid, displayLanes, stageHeight);
}

export function tickExploreGame(
  state: ExploreGameState,
  dtMs: number,
  stageWidth: number,
  stageHeight: number,
  voiceMidi: number | null,
  voicePitch: SingPitchId | null,
  isSinging: boolean,
  now: number
): ExploreTickResult {
  const { activeLanes, displayLanes } = state.level;
  const cx = clingScreenX(stageWidth);
  const gY = groundY(stageHeight);
  const fly = exploreFlyZoneBounds(stageHeight);
  const midY = midActiveY(state, stageHeight);
  const speed = state.level.scrollSpeed;

  if (state.phase === 'introDemo') {
    let callSoundPitch: SingPitchId | null = null;
    let clingTargetY = state.clingTargetY;
    const introNotes = state.introNotes.map((note) => ({
      ...note,
      x: note.x - speed * dtMs,
    }));

    for (const note of introNotes) {
      if (note.played) continue;
      const center = note.x + note.width / 2;
      if (Math.abs(center - cx) <= INTRO_HIT_WINDOW_PX) {
        note.played = true;
        callSoundPitch = note.pitchId;
        clingTargetY = exploreDisplayPitchY(note.pitchId, displayLanes, stageHeight);
      }
    }

    const singingNote = introNotes.find((n) => n.played && n.x + n.width > cx - 24);
    if (singingNote && !callSoundPitch) {
      clingTargetY = exploreDisplayPitchY(singingNote.pitchId, displayLanes, stageHeight);
    } else if (introNotes.every((n) => n.played)) {
      clingTargetY = midY;
    }

    const clingY = lerp(state.clingY, clingTargetY, 0.28);
    const elapsedMs = state.elapsedMs + dtMs;
    const allPlayed = introNotes.length > 0 && introNotes.every((n) => n.played);
    const last = introNotes[introNotes.length - 1];
    const cleared =
      introNotes.length === 0 ||
      (last != null && last.x + last.width < cx - INTRO_HIT_WINDOW_PX);
    const finished = allPlayed && cleared && elapsedMs > 400;

    return {
      state: {
        ...state,
        introNotes,
        clingY,
        clingTargetY,
        elapsedMs,
      },
      finished,
      visualDirty:
        callSoundPitch != null ||
        finished ||
        Math.abs(clingY - state.clingY) > 1.5 ||
        introNotes.some((n, i) => n.played !== state.introNotes[i]?.played),
      callSoundPitch,
      collectedNoteIndex: null,
    };
  }

  if (state.phase === 'countdown') {
    const beat = 600;
    const remaining = 3 - Math.floor(state.elapsedMs / beat);
    const nextElapsed = state.elapsedMs + dtMs;
    const countdownBeat = Math.max(0, remaining);
    return {
      state: {
        ...state,
        elapsedMs: nextElapsed,
        countdownBeat,
        clingY: state.clingY > 0 ? state.clingY : midY,
        clingTargetY: state.clingTargetY > 0 ? state.clingTargetY : midY,
      },
      finished: nextElapsed >= 3 * beat + 200,
      visualDirty: countdownBeat !== state.countdownBeat || state.clingY <= 0,
      callSoundPitch: null,
      collectedNoteIndex: null,
    };
  }

  if (state.phase !== 'play') {
    return {
      state,
      finished: false,
      visualDirty: false,
      callSoundPitch: null,
      collectedNoteIndex: null,
    };
  }

  const scrollX = state.scrollX + speed * dtMs;
  let clingTargetY = state.clingTargetY;
  if (isSinging) {
    const flyY = explorePitchOrMidiToFlyY(voiceMidi, voicePitch, activeLanes, stageHeight);
    if (flyY != null) clingTargetY = flyY;
  } else {
    clingTargetY = Math.min(clingTargetY + CLING_GRAVITY_PX_MS * dtMs * 1000, gY - 20);
  }
  clingTargetY = Math.max(fly.top, Math.min(fly.bottom, clingTargetY));
  const clingY = lerp(state.clingY, clingTargetY, 0.28);

  let score = state.score;
  let feedback = state.feedback;
  let feedbackUntil = state.feedbackUntil;
  if (now >= feedbackUntil) feedback = null;

  let visualDirty = feedback !== state.feedback;
  let items = state.items;
  let collectedNoteIndex: number | null = null;

  for (let i = 0; i < state.items.length; i++) {
    const item = state.items[i]!;
    if (item.collected) continue;

    const itemY = exploreDisplayPitchY(item.pitchId, displayLanes, stageHeight);
    const screenX = item.worldX - scrollX;
    const dx = Math.abs(item.worldX - (scrollX + cx));
    const dy = Math.abs(itemY - clingY);
    const inWindow = dx <= CLING_COLLECT_RADIUS_X && dy <= CLING_COLLECT_RADIUS_Y;

    if (inWindow && isSinging && pitchMatchesCollect(voiceMidi, voicePitch, item.pitchId)) {
      score += 120;
      feedback = 'collect';
      feedbackUntil = now + 240;
      visualDirty = true;
      collectedNoteIndex = i;
      if (items === state.items) items = state.items.slice();
      items[i] = { ...item, collected: true, hit: true };
    } else if (screenX + EXPLORE_NOTE_GAP * 0.2 < cx - CLING_COLLECT_RADIUS_X * 1.5) {
      feedback = 'miss';
      feedbackUntil = now + 240;
      visualDirty = true;
      if (items === state.items) items = state.items.slice();
      items[i] = { ...item, collected: true, missed: true };
    }
  }

  const allCollected = items.every((i) => i.collected);
  const reachedEnd = scrollX >= state.lengthWorld;
  const finished = allCollected || reachedEnd;

  return {
    state: {
      ...state,
      scrollX,
      clingY,
      clingTargetY,
      elapsedMs: state.elapsedMs + dtMs,
      score,
      items,
      feedback,
      feedbackUntil,
      phase: finished ? 'finished' : 'play',
    },
    finished,
    visualDirty: visualDirty || finished,
    callSoundPitch: null,
    collectedNoteIndex,
  };
}

export function exploreCollectedCount(state: ExploreGameState): number {
  return state.items.filter((i) => i.hit).length;
}

export function introPlayedCount(state: ExploreGameState): number {
  return state.introNotes.filter((n) => n.played).length;
}

/** Y en pantalla usando la grilla completa (pistas visibles). */
export function exploreDisplayPitchY(
  pitchId: SingPitchId,
  displayLanes: SingPitchId[],
  stageHeight: number
): number {
  const idx = displayLanes.indexOf(pitchId);
  if (idx < 0) return exploreFlyZoneBounds(stageHeight).top;
  const bandH = exploreLaneBandHeight(stageHeight, displayLanes.length);
  return exploreLaneBandTop(pitchId, displayLanes, stageHeight) + bandH / 2;
}
